import {
  type BaseContentProps,
  type BaseModalProps,
  type ModalContentProps,
  type ModalFooterProps,
  type ModalHeaderProps,
  type ModalProps,
} from '@/features/shared/elements/Modals/types'
import {
  ActionBarContent,
  type ActionBarProps,
  FooterActionBar,
  HeaderActionBar,
  ModalButtonGroup,
  ModalContentWrapper,
} from '@/features/shared/elements/Modals/styles'
import { ArrowLeftIcon, NegativeIcon } from '@shipt/design-system-icons'
import { Button, IconButton, TextButton } from '@shipt/design-system-buttons'
import { isKeyboardUser } from '@/features/shared/utils/detectKeyboardUser'
import { isOnServer } from '@shared/constants/util'
import { FeedbackBanner } from '@shipt/design-system-banner'
import styled, { type IStyledComponent } from 'styled-components'
import { DialogContainerTrackComponent } from '@/features/shared/components/DialogContainer/DialogContainerTrackComponent'

type ChildrenProps = { children?: React.ReactNode }

export const createModal =
  ({ modalFrame: ModalFrame }: BaseModalProps) =>
  ({
    children,
    contentLabel = '',
    isCancellable = true,
    contentRef,
    isOpen = true,
    sublocation = '',
    shouldReturnFocusAfterClose,
    ...restProps
  }: ModalProps & ChildrenProps) => {
    return (
      <ModalFrame
        isOpen={isOpen}
        contentLabel={contentLabel}
        shouldCloseOnOverlayClick={isCancellable}
        shouldCloseOnEsc={isCancellable}
        contentRef={contentRef}
        {...restProps}
        shouldReturnFocusAfterClose={
          shouldReturnFocusAfterClose ?? (!isOnServer() && isKeyboardUser())
        }
      >
        {children}
        {!!contentLabel && (
          <DialogContainerTrackComponent type={contentLabel} sublocation={sublocation} />
        )}
      </ModalFrame>
    )
  }

export const createHeader =
  ({ actionBar: ActionBar } = { actionBar: HeaderActionBar }) =>
  ({
    isCancellable = true,
    hasBackNavigation = false,
    onNavLinkClick,
    navLinkCopy,
    hasStickyActionHeader = false,
    onBackNavigation,
    onRequestClose,
    closeButtonRef,
    surface = 'default',
  }: ModalHeaderProps) => {
    return (
      <ActionBar data-sticky={hasStickyActionHeader}>
        <div>
          {!!hasBackNavigation && (
            <ActionBarContent data-position="left">
              <IconButton
                icon={ArrowLeftIcon}
                aria-label="back"
                onClick={onBackNavigation}
                surface={surface}
                variant="ghost"
              />
            </ActionBarContent>
          )}
          {!!(isCancellable || onNavLinkClick) && (
            <ActionBarContent data-position="right">
              {!!onNavLinkClick && <TextButton onClick={onNavLinkClick}>{navLinkCopy}</TextButton>}

              {!!isCancellable && (
                <IconButton
                  ref={closeButtonRef}
                  icon={NegativeIcon}
                  aria-label="close"
                  onClick={onRequestClose}
                  surface={surface}
                  variant="ghost"
                />
              )}
            </ActionBarContent>
          )}
        </div>
      </ActionBar>
    )
  }

// Refactor modal title to be a heading tag
export const createContent =
  ({ size, title: Title }: BaseContentProps) =>
  ({
    children,
    hasHeader,
    hasFooter,
    modalTitle,
    error,
    errorTitle = '',
    titleProps,
    className,
    successMessage,
    successMessageTitle = '',
    onDismissErrorBanner,
  }: ModalContentProps & ChildrenProps) => {
    return (
      <ModalContentWrapper
        data-size={size}
        hasHeader={hasHeader}
        hasFooter={hasFooter}
        className={className}
      >
        {Boolean(!error?.message && successMessage) && (
          <ModalBanner
            variant="positive"
            aria-live="assertive"
            hasIcon
            title={successMessageTitle}
            description={successMessage}
          />
        )}
        {!!error?.message && (
          <ModalBanner
            title={errorTitle}
            variant="negative"
            hasIcon
            aria-live="assertive"
            description={error?.message}
            onDismiss={onDismissErrorBanner}
          />
        )}
        {Boolean(modalTitle) && (
          <Title {...titleProps} as="h2">
            {modalTitle}
          </Title>
        )}
        {children}
      </ModalContentWrapper>
    )
  }

export const createFooter =
  ({
    actionBar: ActionBar = FooterActionBar,
    buttonGroup: ButtonGroup = ModalButtonGroup,
  }: {
    actionBar?: IStyledComponent<
      'web',
      {
        children: React.ReactNode
        className?: string
        footerAsOverlay: boolean
      } & ActionBarProps
    >
    buttonGroup?: IStyledComponent<'web', Record<string, unknown>>
  }) =>
  ({
    hasStickyActionFooter = true,
    primaryActionCopy,
    secondaryActionCopy,
    onPrimaryClick,
    onSecondaryClick,
    isSecondaryActionEmphasized = false,
    secondaryStyle = 'standard',
    isLoading,
    isSecondaryLoading,
    isPrimaryDisabled = false,
    isSecondaryDisabled = false,
    primaryAriaLabel,
    secondaryAriaLabel,
    footerAsOverlay = false,
    renderBottomArea,
    renderTopArea,
    isNegative = false,
    primaryType,
    formId,
    className,
  }: ModalFooterProps) => {
    return (
      <ActionBar
        data-sticky={hasStickyActionFooter}
        data-position="bottom"
        footerAsOverlay={footerAsOverlay}
        className={className}
      >
        {renderTopArea?.()}
        <ButtonGroup>
          <div>
            <Button
              onClick={onPrimaryClick}
              loading={isLoading}
              disabled={isPrimaryDisabled}
              aria-label={primaryAriaLabel}
              {...(isNegative
                ? {
                    variant: 'primary',
                    concept: 'negative',
                  }
                : {
                    variant: isSecondaryActionEmphasized ? 'secondary' : 'primary',
                  })}
              {...(primaryType && { type: primaryType })}
              {...(formId && { form: formId, type: 'submit' })}
            >
              {primaryActionCopy}
            </Button>
            {!!onSecondaryClick && !!secondaryActionCopy && secondaryStyle === 'standard' && (
              <Button
                variant={isSecondaryActionEmphasized ? 'primary' : 'secondary'}
                onClick={onSecondaryClick}
                loading={isSecondaryLoading}
                disabled={isSecondaryDisabled}
                aria-label={secondaryAriaLabel}
              >
                {secondaryActionCopy}
              </Button>
            )}
            {!!onSecondaryClick && !!secondaryActionCopy && secondaryStyle === 'text' && (
              <TextButton className="modal-secondary-text-button" onClick={onSecondaryClick}>
                {secondaryActionCopy}
              </TextButton>
            )}
            {renderBottomArea?.()}
          </div>
        </ButtonGroup>
      </ActionBar>
    )
  }

const ModalBanner = styled(FeedbackBanner)`
  margin-bottom: 1rem;
  position: relative;
`
